import {
  Component,
  Input,
  OnInit,
  HostBinding,
  ViewChild,
  ViewEncapsulation,
  forwardRef,
} from '@angular/core';
import { SelectOption } from '@platform/types';
import { AUTOCOMPLETE_CHECK_TYPE } from './emma-textarea.component';
import { EMMAInputComponent } from './emma-input.component';
import { isNil, noop } from 'lodash';
import { shouldBeNumber } from '@platform/helpers/number.helper';
import { EMMAFormElementComponent } from './emma-form-element.component';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  ValidatorFn,
} from '@angular/forms';

const VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAInputAutocompleteComponent),
  multi: true,
};
const VALIDATOR = {
  provide: NG_VALIDATORS,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAInputAutocompleteComponent),
  multi: true,
};

@Component({
    selector: 'emma-input-autocomplete',
    templateUrl: './emma-input-autocomplete.component.html',
    styleUrls: ['./emma-input-autocomplete.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [VALUE_ACCESSOR, VALIDATOR],
    standalone: false
})
export class EMMAInputAutocompleteComponent
  extends EMMAFormElementComponent
  implements OnInit, ControlValueAccessor, Validator
{
  @ViewChild('inputElement') inputElement!: EMMAInputComponent;
  @ViewChild(EMMAInputComponent) emmaInput!: EMMAInputComponent;

  @HostBinding('class') override containerClass = 'form-group m-form__group m-form__group--md';

  // Autocomplete
  @Input() autocompleteItems: Array<SelectOption> = [];
  @Input() autocompleteButtonLabel = '';
  @Input() autocompleteButtonIcon = '';
  @Input() autocompleteNotFound = '';
  @Input() autocompleteGroupBy?: string;
  @Input() autocompleteCheckItems = false;
  @Input() autocompletTagsItems = false;
  @Input() autocompleteCheckItemsModalType = AUTOCOMPLETE_CHECK_TYPE.AUDIENCE_TAGS;
  @Input() autocompleteCheckIcon = '';
  @Input() autocompleteCheckPopoverMsg = '';

  @Input() first = false;
  @Input() label = '';
  @Input() type = '';
  @Input() inputmode = '';
  @Input() isWeburl?: boolean;

  @Input() get value(): string {
    return this._innerValue;
  }
  set value(v: string) {
    if (v !== this._innerValue) {
      this._innerValue = v ?? '';
      this.onChangeCallback(v);
    }
  }

  showCheckModal = false;

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: string) => void = noop;
  private _innerValue = '';

  override ngOnInit(): void {
    this.updateClasses();
    if (this.type === 'number') {
      this.type = 'text';
    }
  }

  override updateClasses() {
    this._containerClass = [
      'emma-input-autocomplete',
      this.containerClass,
      this.first ? 'm-form__group--first' : '',
    ];
  }

  onSelectAutocompleteTag(text: string) {
    const nativeElement = this.inputElement.inputElement.nativeElement;
    if (nativeElement) {
      if (isNil(this.value)) {
        this.value = '';
      }
      const ieDocument: any = document;
      if (ieDocument.selection) {
        // IE
        nativeElement.focus();
        const sel = ieDocument.selection.createRange();
        sel.text = text;
        this.value = nativeElement.value;
      } else if (shouldBeNumber(nativeElement.selectionStart)) {
        // Others
        const startPos = Number(nativeElement.selectionStart);
        const endPos = Number(nativeElement.selectionEnd);
        // Elimina el espacio al final si es una substitución
        if (startPos !== endPos) {
          text = text.trim();
        }
        this.value =
          this.value.substring(0, startPos) + text + this.value.substring(endPos, this.value.length);
        // Para que ocurra después de que Angular reasigne el contenido
        window.requestAnimationFrame(() => {
          nativeElement.selectionStart = startPos + text.length;
          nativeElement.selectionEnd = startPos + text.length;
        });
      } else {
        this.value += text;
      }
      nativeElement.focus();
    }
  }
  onCheckItems(): void {
    this.showCheckModal = true;
  }
  onCancelCheckItems(): void {
    this.showCheckModal = false;
  }
  // From ControlValueAccessor interface
  writeValue(value: string): void {
    if (value !== this._innerValue) {
      this._innerValue = value ?? '';
    }
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  onTouched(): void {
    this.onTouchedCallback();
  }

  override validate: ValidatorFn = (control: AbstractControl) => {
    return this.emmaInput ? this.emmaInput.validate(control) : null;
  };
}
